import { buildSort, goPromise } from "@delanobgt/admin-core/util/helpers";
import {
  ApiClientFactory,
  IMeta,
  IPagination,
  ISort,
} from "@delanobgt/admin-core/util/types";
import { CancelTokenSource } from "axios";
import _ from "lodash";

import {
  EPackageListSortField,
  IPackageList,
  PPackageListFilter,
} from "../entities";
import IPackageListRepository from "../repository";
import { IPackageListSendRequest } from "../request/request";

export default class PackageListRepository
  implements IPackageListRepository<CancelTokenSource> {
  private apiClientFactory: ApiClientFactory;

  // api routes
  private PACKAGE_LIST_ROUTE = "/admin/package-list";

  constructor(apiClientFactory: ApiClientFactory) {
    this.apiClientFactory = apiClientFactory;
  }

  buildFilter = (filter: PPackageListFilter) => {
    return filter;
  };

  private getSignalToken(signal: CancelTokenSource) {
    return _.get(signal, "token", null);
  }

  private getError(error: any) {
    return _.get(error, "response.data.errors", "Something went wrong.");
  }

  async getPackageList(
    pagination: IPagination,
    filter: PPackageListFilter,
    sorts: ISort<EPackageListSortField>[],
    signal?: CancelTokenSource
  ): Promise<[IPackageList[], IMeta]> {
    const sort = buildSort(sorts);
    const params = _.pickBy(
      {
        ...pagination,
        ...this.buildFilter(filter),
      },
      Boolean
    );
    const [err, response] = await goPromise(
      this.apiClientFactory({ cancelToken: this.getSignalToken(signal) }).get(
        `${this.PACKAGE_LIST_ROUTE}?sort=${sort}`,
        {
          params,
        }
      )
    );
    if (err) {
      throw this.getError(err);
    }
    const { data, meta } = response.data;
    return [data, meta];
  }

  async getPackageListById(id: number): Promise<IPackageList> {
    const [err, response] = await goPromise(
      this.apiClientFactory().get(`${this.PACKAGE_LIST_ROUTE}/${id}`)
    );
    if (err) {
      throw this.getError(err);
    }
    return response.data;
  }

  async createPackageList(formData: FormData): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().post(`${this.PACKAGE_LIST_ROUTE}`, formData)
    );
    if (err) {
      throw this.getError(err);
    }
  }

  async updatePackageList(id: number, formData: FormData): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().patch(
        `${this.PACKAGE_LIST_ROUTE}/${id}`,
        formData
      )
    );
    if (err) {
      throw this.getError(err);
    }
  }

  async sendPackageList(
    id: number,
    sendRequest: IPackageListSendRequest
  ): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().post(
        `${this.PACKAGE_LIST_ROUTE}/${id}/send-package`,
        sendRequest
      )
    );
    if (err) {
      throw this.getError(err);
    }
  }
}
